import { useState, useEffect, useCallback } from "react";

export const useLocationPermission = () => {
  const [latitude, setLatitude] = useState(null);
  const [longitude, setLongitude] = useState(null);
  const [permissionStatus, setPermissionStatus] = useState(null); // 'granted', 'denied', 'pending', 'error'
  const [isPermissionCheckComplete, setIsPermissionCheckComplete] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);

  // Function to request location
  const requestLocation = useCallback(() => {
    setPermissionStatus("pending");
    setErrorMessage(null);

    if (!navigator.geolocation) {
      setPermissionStatus("error");
      setErrorMessage("Geolocation is not supported by your browser");
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        setLatitude(position.coords.latitude);
        setLongitude(position.coords.longitude);
        setPermissionStatus("granted");
        setErrorMessage(null);
      },
      (error) => {
        let errorMsg = "Unable to retrieve your location";
        
        if (error.code === error.PERMISSION_DENIED) {
          setPermissionStatus("denied");
          errorMsg = "Location permission was denied. Please enable location services in your browser settings.";
        } else if (error.code === error.POSITION_UNAVAILABLE) {
          setPermissionStatus("error");
          errorMsg = "Your location information is unavailable.";
        } else if (error.code === error.TIMEOUT) {
          setPermissionStatus("error");
          errorMsg = "The request to get your location timed out.";
        }
        
        setErrorMessage(errorMsg);
        setLatitude(null);
        setLongitude(null);
      },
      {
        timeout: 10000, // 10 second timeout
        enableHighAccuracy: false, // Don't require high accuracy for faster results
        maximumAge: 300000, // Cache location for 5 minutes
      }
    );
  }, []);

  // Check permission status on mount
  useEffect(() => {
    // Check if we already have permission via permissions API (if available)
    if (navigator.permissions && navigator.permissions.query) {
      navigator.permissions
        .query({ name: "geolocation" })
        .then((result) => {
          if (result.state === "granted") {
            setPermissionStatus("granted");
            requestLocation();
          } else if (result.state === "denied") {
            setPermissionStatus("denied");
            setErrorMessage(
              "Location permission was denied. Please enable location services in your browser settings."
            );
          } else {
            setPermissionStatus("pending");
            requestLocation();
          }
        })
        .catch(() => {
          // Fallback: Just try to get location
          requestLocation();
        });
    } else {
      // Fallback for browsers without permissions API
      requestLocation();
    }
    
    setIsPermissionCheckComplete(true);
  }, []);

  // Retry function
  const retry = useCallback(() => {
    setLatitude(null);
    setLongitude(null);
    requestLocation();
  }, [requestLocation]);

  return {
    latitude,
    longitude,
    permissionStatus,
    isPermissionCheckComplete,
    errorMessage,
    retry,
    isLocationAvailable: latitude !== null && longitude !== null,
  };
};
